import {
  CalculationPickingInsightsSummaryResponse,
  CalculationPickingIssuesResponse,
  CalculationStatisticClusterType,
  PickingIssueResponse,
  RoutesExecutionSummaryResponse,
  StorePickingInsightsResponse,
} from '@ydistri/api-sdk';
import { getTags } from '../../../apis/apiLib';
import { apiSlice } from '../../../apis/api';
import { CalculationsCollection } from '../../../swagger/collections';
import { computePercentage } from '@ydistri/generic-utils';
import { CalculationPickingExtendedResponse, PickingResultsExtended } from './pickingResultsLib';

const { TAGS, TAGS_ARRAY } = getTags('calculationPickingResults');

export interface GetPickingResultsRequest {
  calculationId: number;
  departmentId?: number;
  clusterType?: CalculationStatisticClusterType;
}

export interface GetTargetStoresPickingDetailsRequest extends GetPickingResultsRequest {
  sourceStoreId: number;
}

export interface GetPickingResultsSummaryRequest {
  calculationId: number;
}

export const computePickingResultsPercentages = (
  response: RoutesExecutionSummaryResponse,
): PickingResultsExtended => {
  return {
    ...response,
    executedQuantityPercentage: computePercentage(
      response.executedQuantity,
      response.totalQuantity,
    ),
    executedValuePercentage: computePercentage(response.executedValue, response.totalValue),
    executedSkuCountPercentage: computePercentage(
      response.executedSkuCount,
      response.totalSkuCount,
    ),
  };
};

export const apiCalculationPickingResults = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getPickingResultsSummary: builder.query<
        CalculationPickingExtendedResponse,
        GetPickingResultsSummaryRequest
      >({
        queryFn: async ({ calculationId }) => {
          const { data } = await CalculationsCollection.getCalculationPicking(calculationId);

          const result: CalculationPickingExtendedResponse = {
            ...data.data,
            pickingResults: computePickingResultsPercentages(data.data.pickingResults),
          };

          return {
            data: result,
          };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationPickingResultsSummary,
            id: `pickingSummary-${arg.calculationId}`,
          },
        ],
      }),
      getPickingInsights: builder.query<StorePickingInsightsResponse[], number>({
        queryFn: async (calculationId: number) => {
          const response =
            await CalculationsCollection.getCalculationPickingInsights(calculationId);
          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationPickingResultsSummary,
            id: `pickingInsights-${arg}`,
          },
        ],
      }),
      getTargetStoresPickingInsights: builder.query<
        StorePickingInsightsResponse[],
        GetTargetStoresPickingDetailsRequest
      >({
        queryFn: async ({ calculationId, sourceStoreId }) => {
          const response = await CalculationsCollection.getSourceStorePickingInsights(
            calculationId,
            sourceStoreId,
          );
          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationStoreTargetStorePickingInsights,
            id: `targetStoresPickingInsights-${arg.calculationId}-${arg.sourceStoreId}`,
          },
        ],
      }),
      getPickingInsightsSummary: builder.query<
        CalculationPickingInsightsSummaryResponse,
        GetPickingResultsSummaryRequest
      >({
        queryFn: async ({ calculationId }) => {
          const response =
            await CalculationsCollection.getCalculationPickingInsightsSummary(calculationId);
          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationPickingInsightsSummary,
            id: `pickingInsightsSummary-${arg.calculationId}`,
          },
        ],
      }),
      getPickingIssuesSummary: builder.query<CalculationPickingIssuesResponse[], number>({
        queryFn: async (calculationId: number) => {
          const response = await CalculationsCollection.getCalculationPickingIssues(calculationId);
          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationPickingIssues,
            id: `pickingIssuesSummary-${arg}`,
          },
        ],
      }),
      getPickingIssues: builder.query<PickingIssueResponse[], number>({
        queryFn: async (calculationId: number) => {
          const response = await CalculationsCollection.getSkusWithIssues(calculationId);
          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationPickingIssues,
            id: `pickingIssues-${arg}`,
          },
        ],
      }),
      getSourceStorePickingIssues: builder.query<
        PickingIssueResponse[],
        GetTargetStoresPickingDetailsRequest
      >({
        queryFn: async ({ calculationId, sourceStoreId }) => {
          const response = await CalculationsCollection.getSkusWithIssuesForSourceStore(
            calculationId,
            sourceStoreId,
          );
          return { data: response.data.data };
        },
        providesTags: (result, error, arg) => [
          {
            type: TAGS.calculationSourceStorePickingIssues,
            id: `sourceStorePickingIssues-${arg.calculationId}-${arg.sourceStoreId}`,
          },
        ],
      }),
    }),
  });

export const {
  useGetPickingResultsSummaryQuery,
  useGetPickingInsightsQuery,
  useGetTargetStoresPickingInsightsQuery,
  useGetPickingInsightsSummaryQuery,
  useGetSourceStorePickingIssuesQuery,
  useGetPickingIssuesSummaryQuery,
  useGetPickingIssuesQuery,
} = apiCalculationPickingResults;
